import { format } from 'date-fns'

import { Button, Notice, Tooltip } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { canPerform } from '~publish/utils/permissions/canPerform'

import type { FeatureAccessDisabledReason } from '../graphqlEnums'

import type { JSX } from 'react'

/**
 * The channel fields needed to decide whether channel-level analytics are
 * blocked and to refresh the connection. Unmasked so callers can pass a channel
 * from any query that spreads this fragment without going through
 * `readFragment`.
 */
export const InsightsPermissionBanner_Channel = graphql(/* GraphQL */ `
  fragment InsightsPermissionBanner_Channel on Channel @_unmask {
    id
    name
    service
    apiVersion
    isDisconnected
    allowedActions
    featureAccess {
      channelAnalytics {
        reason
        since
        requiredScopes
      }
    }
  }
`)

type InsightsPermissionBannerChannel = ResultOf<
  typeof InsightsPermissionBanner_Channel
>

const REFRESH_CTA =
  'publish-insightsChannel-insightsPermissionBanner-refreshConnection-1'

// Why Buffer lost read access to this channel's analytics. The phrase slots
// into "...as of {date}, due to {cause}."
const CAUSE_BY_REASON: Record<FeatureAccessDisabledReason, string> = {
  missingScope: 'a missing permission',
  permissionRevoked: 'a permission change on the platform',
}

const NO_RECONNECT_PERMISSION =
  "You don't have permission to refresh the connection. Please contact the administrator of your organization."

type InsightsPermissionBannerProps = {
  channel: InsightsPermissionBannerChannel
}

/**
 * Targeted notice on the single-channel Insights page shown when channel-level
 * analytics are blocked (missing scope or revoked permission). It surfaces when
 * Buffer lost read access and offers a connection refresh — the same OAuth
 * refresh-window flow the disconnected-channel notices use — that restores
 * analytics. Renders nothing when analytics are fine, or when the channel is
 * disconnected (the existing disconnected-channel banner owns that state).
 */
export function InsightsPermissionBanner({
  channel,
}: InsightsPermissionBannerProps): JSX.Element | null {
  const refreshChannelConnection = useRefreshChannelConnection()

  const channelAnalytics = channel.featureAccess?.channelAnalytics

  // Analytics work, or the channel is fully disconnected (owned by the
  // existing disconnected-channel banner) — nothing to show here.
  if (!channelAnalytics || channel.isDisconnected) {
    return null
  }

  const lostAccessDate = format(new Date(channelAnalytics.since), 'MMM d, yyyy')

  // The notice itself explains stale metrics and is useful to everyone; only
  // the reconnect action needs the per-channel authority. Mirrors Community's
  // `ChannelRefreshNotice`, which gates the same OAuth flow.
  const canReconnect = canPerform(channel, 'reconnectChannel')

  const handleRefresh = (): void => {
    if (!canReconnect) return
    refreshChannelConnection({
      channel: {
        id: channel.id,
        service: channel.service,
        name: channel.name,
        apiVersion: channel.apiVersion,
      },
      cta: REFRESH_CTA,
    })
  }

  return (
    <Notice variant="warning">
      <Notice.Text>
        {`Buffer no longer has access to this channel's analytics as of ${lostAccessDate}, due to ${CAUSE_BY_REASON[channelAnalytics.reason]}. Refresh your connection to see your updated metrics.`}
      </Notice.Text>
      <Notice.Actions>
        {canReconnect ? (
          <Button size="small" onClick={handleRefresh}>
            Refresh Connection
          </Button>
        ) : (
          <Tooltip content={NO_RECONNECT_PERMISSION}>
            {/* span wrapper so the Tooltip receives pointer events even when
                the Button is `disabled` (native disabled buttons don't fire
                hover/focus events). The reason is repeated in the button's
                accessible name because neither the disabled button nor this
                wrapper can take focus, so the tooltip is unreachable by
                keyboard. */}
            <span>
              <Button
                size="small"
                disabled
                aria-label={`Refresh Connection. ${NO_RECONNECT_PERMISSION}`}
              >
                Refresh Connection
              </Button>
            </span>
          </Tooltip>
        )}
      </Notice.Actions>
    </Notice>
  )
}

InsightsPermissionBanner.displayName = 'InsightsPermissionBanner'
